import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from 'antd';
import dayjs from 'dayjs';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { tarihEtiketi } from '../../satis-analiz/ZamanBlok';
import { miktarMetni, oranMetni } from '../../satis-analiz/bicim';
import {
  AramaKutusu, aramaSuz, DetayBolum, DetayMesaj, DetayYukleniyor, GunlukCubuklar, OzetKutulari,
  type CubukNoktasi,
} from '../../satis-analiz/detay/ortak';
import type { Donem } from '../../satis-analiz/donem';
import { queryDataSource, type QueryResponse } from '../../../api/query';
import type { StokKaynaklari } from '../ekranKaynaklari';
import { bildiriyor, stokParametreleri, stokQueryKey, type StokKaynak, type StokSorguFiltresi } from '../useStokVerisi';
import { kategoriNormalize, kategoriSuz } from '../kategori';
import {
  OLU_TURLERI, donusGunu, oluKovasi, stokGunAyir,
  type OluStokSatiri, type OluTur, type StokGunSatiri, type SubeFarkSatiri,
} from '../toplama';
import { gunMetni } from '../bicimYardimcilari';
import type { DevirSatiri } from '../AkisBlok';
import { ayEtiketi } from '../KayipBlok';
import type { StokDetay } from './stokDetayTipleri';
import { KayipTumUrunler } from './KayipTumUrunler';

/**
 * G4b/C4a — Stok Analizi detay modalı (Satış Analiz `detay/DetayModal.tsx` kalıbı OKUNDU, kopyalanmadı).
 * `StokDetay`'in dört varyantı geri yığını GEREKTİRMEZ (hiçbiri birbirine geçiş açmıyor — SA'daki
 * kategori→ürün gibi bir zincir yok) — bu yüzden modal SA'dan daha sade: tek seviye.
 *
 * 🔴 S9 — MODAL İÇİNDE recharts YOK (`ortak.tsx:82-87` notu: antd Modal açılış animasyonunda
 * `ResponsiveContainer` 0 genişlik ölçer). Kategori/gün kırılımları `GunlukCubuklar` (saf CSS) ile
 * çizilir — bileşen "günlük" diye anılsa da (`ortak.tsx`) kategorik herhangi bir seri için kullanılabilir
 * ({anahtar,etiket,deger,baslik} genel sözleşmesi).
 */

// ════════════════════════════════════════════════════════════════════════════════════════════
// Ortak: kaynak sorgu kancası (990061-990065 — `useStokSorgu`'nun eşi, ekstra parametre (@STOK/@KOVA)
// enjekte edebilmek için burada AYRI: `useStokVerisi.ts`'in `StokSorguFiltresi`si @STOK/@KOVA taşımaz.)
//
// 🔴 Düzeltme turu 2/5, F1 kök-neden kapatması — `ekstra` da `stokParametreleri`nin KULLANDIĞI AYNI
// `bildiriyor()` kapısından geçer: ds'in bildirmediği bir `ekstra` anahtarı `queryDataSource`'a HİÇ
// GİTMEZ (dünkü Critical'ın kök nedeni buydu — 990061'e enjekte edilen `@TUR` ds tarafından
// bildirilmediği için backend'de sessizce düşmüştü, istemci hâlâ "gönderdim" sanıyordu). ÇALIŞMA
// ZAMANINDA FIRLATILMAZ (backend'in zaten yaptığının aynısı, davranış değişmez) — kazanç, artık
// TESTLE kapılanabilir olması.
// ════════════════════════════════════════════════════════════════════════════════════════════

/**
 * F1 kök-neden kapatması, saf fonksiyon (birim testli) — `ekstra`yı `bildiriyor()` kapısından geçirir.
 * `kaynak` yoksa ya da bir anahtar bildirilmiyorsa o anahtar ÇIKTIDA HİÇ YER ALMAZ (fırlatmaz, sessizce
 * düşer — backend'in zaten yaptığının aynısı; kazanç, bunun artık TESTLE kapılanabilir olması).
 */
export function ekstraSuzulmus(kaynak: StokKaynak | undefined, ekstra: Record<string, string>): Record<string, string> {
  if (!kaynak) return {};
  const cikti: Record<string, string> = {};
  for (const [ad, deger] of Object.entries(ekstra)) {
    if (bildiriyor(kaynak, ad)) cikti[ad] = deger;
  }
  return cikti;
}

function useDetaySorgu<T>(
  kaynak: StokKaynak | undefined,
  f: StokSorguFiltresi,
  ekstra: Record<string, string>,
  enabled: boolean,
) {
  const parametreler = kaynak ? { ...stokParametreleri(kaynak, f), ...ekstraSuzulmus(kaynak, ekstra) } : {};
  return useQuery<QueryResponse<T>>({
    queryKey: stokQueryKey(kaynak?.sourceId, parametreler),
    queryFn: () => queryDataSource<T>({ sourceId: kaynak!.sourceId, parameters: parametreler }),
    enabled: !!kaynak && enabled,
    throwOnError: false,
  });
}

interface GovdeProps {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube: string | null;
  kategori: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Varyant 1 — kayipUrun: ürünün fire belgeleri (990063, @STOK + @TUR='fire')
// ════════════════════════════════════════════════════════════════════════════════════════════

interface KayipBelgeSatiri {
  fis_no: number | string;
  tarih: string;
  lokasyon: string;
  tur: string | null;
  miktar: number;
  birim_fiyat: number;
  tutar: number;
}

/** 990063 `ORDER BY ... LIMIT 200` (SQL 144). */
export const KAYIP_BELGE_SINIRI = 200;

function KayipUrunDetay({
  kaynaklar, donem, sube, kategori, stokKod,
}: GovdeProps & { stokKod: string }) {
  const kaynak = kaynaklar.kayipBelge;
  const f: StokSorguFiltresi = { donem, sube, kategori };
  const q = useDetaySorgu<KayipBelgeSatiri>(kaynak, f, { '@STOK': stokKod, '@TUR': 'fire' }, true);

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">Fire belgeleri bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">Fire belgeleri yüklenemedi.</DetayMesaj>;
  const satirlar = q.data?.data ?? [];
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Dönemde bu ürünün fire belgesi yok.</DetayMesaj>;

  const toplamTutar = satirlar.reduce((t, s) => t + (Number(s.tutar) || 0), 0);
  const toplamMiktar = satirlar.reduce((t, s) => t + (Number(s.miktar) || 0), 0);

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-tutar', etiket: 'Toplam tutar', deger: fmtTRY(toplamTutar) },
          { testid: 'stok-detay-ozet-miktar', etiket: 'Toplam miktar', deger: miktarMetni(toplamMiktar) },
          { testid: 'stok-detay-ozet-belge', etiket: 'Belge sayısı', deger: fmtNum(satirlar.length) },
        ]}
      />
      <DetayBolum baslik="Fire belgeleri">
        <div className="overflow-x-auto">
          <table data-testid="stok-detay-belgeler" className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3 whitespace-nowrap">Fiş no</th>
                <th className="text-left font-medium py-2 px-3">Tarih</th>
                <th className="text-left font-medium py-2 px-3">Şube</th>
                <th className="text-right font-medium py-2 px-3">Miktar</th>
                <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Birim fiyat</th>
                <th className="text-right font-medium py-2 pl-3">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s, i) => (
                <tr key={`${String(s.fis_no)}-${i}`} data-testid="stok-detay-belge" className="border-b border-border last:border-b-0">
                  <td className="py-2 pr-3 tnum whitespace-nowrap">{String(s.fis_no)}</td>
                  <td className="py-2 px-3 tnum whitespace-nowrap">{tarihEtiketi(String(s.tarih))}</td>
                  <td className="py-2 px-3">{s.lokasyon}</td>
                  <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.miktar) || 0)}</td>
                  <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.birim_fiyat) || 0)}</td>
                  <td className="py-2 pl-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.tutar) || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {satirlar.length >= KAYIP_BELGE_SINIRI && (
          <p data-testid="stok-detay-belge-notu" className="text-[12px] text-ink-500">
            Yalnız son {KAYIP_BELGE_SINIRI} belge satırı gösteriliyor.
          </p>
        )}
      </DetayBolum>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Varyant 2 — kayipGun: o günün/ayın ürün + kategori kırılımı
//
// 🔴 Düzeltme turu 1/5, F1 — 990061 (kayıpKategori) `@TUR` NE BİLDİRİR NE METNİNDE KULLANIR
// (144-gizova-kayip-ds.sql:92-104: parametreleri yalnız @BASTARIH/@BITTARIH/@LOKASYON; `@TUR`
// gönderilse backend'in `CdcReportQueryService.cs` katmanı sessizce düşürür, hata VERMEZ). ds
// hem 'fire' hem 'sarf' satırını GERİ DÖNDÜRÜR (`fis_turu` 16/15 ikisi de). `KayipBlok.tsx:277`
// tam bu yüzden `rows.filter((r) => r.tur === 'fire')` yapıyor — burada da AYNI süzgeç şart,
// yoksa özet/`GunlukCubuklar` sarfı da toplar. 990062 (kayıpUrun) FARKLI: `@TUR`u GERÇEKTEN
// bildirir ve WHERE'de kullanır (`144-gizova-kayip-ds.sql:118-126`) — oraya `@TUR:'fire'`
// göndermek DOĞRU, dokunulmadı.
// ════════════════════════════════════════════════════════════════════════════════════════════

interface KayipKategoriSatiri { kategori: string; tur: string; urun_sayisi: number; miktar: number; tutar: number }
interface KayipUrunSatiri {
  stok_kodu: string; urun: string; kategori: string; miktar: number; tutar: number; maliyet: number;
  kumulatif_pay: number | null;
}

/** 'gun' → o gün; 'ay' → ayın ilk↔son günü (spec: "aynı ds'ler, ayın ilk ↔ son günü penceresiyle"). */
export function kayipGunPenceresi(d: { kova: 'gun'; gun: string } | { kova: 'ay'; ay: string }): { bas: string; bit: string } {
  if (d.kova === 'gun') return { bas: d.gun, bit: d.gun };
  const ilk = dayjs(`${d.ay}-01`);
  return { bas: ilk.format('YYYY-MM-DD'), bit: ilk.endOf('month').format('YYYY-MM-DD') };
}

function KayipGunDetay({
  kaynaklar, sube, kategori, pencere,
}: Omit<GovdeProps, 'donem'> & { pencere: { bas: string; bit: string } }) {
  const donem: Donem = { bas: pencere.bas, bit: pencere.bit, tekGun: pencere.bas === pencere.bit };
  const f: StokSorguFiltresi = { donem, sube, kategori };
  const katKaynak = kaynaklar.kayipKategori;
  const urunKaynak = kaynaklar.kayipUrun;
  const [arama, setArama] = useState('');

  // 990061 @TUR bildirmiyor → GÖNDERİLMEZ (bildirim kapısı zaten engeller); dönen satırlar
  // fire+sarf karışık, aşağıda İSTEMCİDE `tur === 'fire'` ile süzülür (F1).
  const katQ = useDetaySorgu<KayipKategoriSatiri>(katKaynak, f, {}, true);
  const urunQ = useDetaySorgu<KayipUrunSatiri>(urunKaynak, f, { '@TUR': 'fire' }, true);

  if (!katKaynak && !urunKaynak) {
    return <DetayMesaj testid="stok-detay-kaynak-yok">Kayıp kırılımı bu firmada tanımlı değil.</DetayMesaj>;
  }
  if (katQ.isLoading || urunQ.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;

  // F1 — 990061 fire+sarf karışık döner, istemcide süzülür. F2 — kategori `kategoriNormalize`den geçer
  // ('(grup yok)' → '(kategorisiz)'), ekranın geri kalanıyla (AkisBlok/SermayeBlok/KayipBlok) aynı etiket.
  const katSatirlar = (katQ.data?.data ?? [])
    .filter((s) => s.tur === 'fire')
    .map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  const urunSatirlar = (urunQ.data?.data ?? []).map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));

  /**
   * I-1 (final-fix F-2, nokta 2) — 990061 (`katKaynak`) `@KATEGORI` BİLDİRMEZ (144-gizova-kayip-ds.sql:
   * 92-104): kategori süzgeci doluyken `katSatirlar` YİNE TÜM kategorilerin toplamıdır. 990062
   * (`urunKaynak`) `@KATEGORI`yi BİLDİRİR VE KULLANIR — aynı satırın hemen altındaki "Ürün kırılımı"
   * zaten o süzülmüş pencereden geliyordu. Eskiden "Toplam fire" katSatirlar'dan (süzülmemiş)
   * hesaplanıyordu: kategori seçiliyken modal içinde BİRBİRİNİ TUTMAYAN iki sayı yan yana duruyordu
   * (`*KAHVALTILIK` örneğinde 134 kat fark). ÇÖZÜM SEÇİMİ: not eklemek yerine "Toplam fire"yı da
   * 990062'nin süzülmüş toplamından türet — AYNI PENCEREDEN iki tutarlı sayı, kullanıcı bir kartla bir
   * modal içinde iki farklı taban okumak zorunda kalmaz (bkz. final-fix-report.md, I-1 gerekçesi).
   * `urunQ` hata verirse ya da bu ds tanımlı değilse eski (süzülmemiş) katSatirlar tabanına DÜŞER —
   * en azından "bir şey" gösterir, sessizce 0 basmaz.
   */
  const kategoriSuzulmusTutarKaynagiKullanilsin = kategori !== null && !!urunKaynak && !urunQ.isError;
  const toplamTutar = kategoriSuzulmusTutarKaynagiKullanilsin
    ? urunSatirlar.reduce((t, s) => t + (Number(s.tutar) || 0), 0)
    : katSatirlar.reduce((t, s) => t + (Number(s.tutar) || 0), 0);
  const toplamUrun = kategoriSuzulmusTutarKaynagiKullanilsin
    ? urunSatirlar.length
    : katSatirlar.reduce((t, s) => t + (Number(s.urun_sayisi) || 0), 0);

  const gorunenUrunler = aramaSuz(urunSatirlar, arama, (u) => [u.urun, u.stok_kodu]);

  const noktalar: CubukNoktasi[] = [...katSatirlar]
    .sort((a, b) => (Number(b.tutar) || 0) - (Number(a.tutar) || 0))
    .map((s) => ({
      anahtar: s.kategori,
      etiket: s.kategori,
      deger: Number(s.tutar) || 0,
      baslik: `${s.kategori}: ${fmtTRY(Number(s.tutar) || 0)}`,
    }));

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-tutar', etiket: 'Toplam fire', deger: fmtTRY(toplamTutar) },
          { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(toplamUrun) },
        ]}
      />
      {katQ.isError ? (
        <DetayMesaj testid="stok-detay-kategori-hata">Kategori kırılımı yüklenemedi.</DetayMesaj>
      ) : katSatirlar.length === 0 ? (
        <DetayMesaj testid="stok-detay-kategori-bos">Bu dönemde kategori kırılımı yok.</DetayMesaj>
      ) : (
        <DetayBolum baslik="Kategori kırılımı">
          <GunlukCubuklar testid="detay-gun-cubuklar" noktalar={noktalar} />
        </DetayBolum>
      )}
      <DetayBolum baslik="Ürün kırılımı">
        {!urunKaynak ? (
          <DetayMesaj testid="stok-detay-urun-kaynak-yok">Ürün kırılımı bu firmada tanımlı değil.</DetayMesaj>
        ) : urunQ.isError ? (
          <DetayMesaj testid="stok-detay-urun-hata">Ürün kırılımı yüklenemedi.</DetayMesaj>
        ) : urunSatirlar.length === 0 ? (
          <DetayMesaj testid="stok-detay-urun-bos">Bu dönemde ürün kırılımı yok.</DetayMesaj>
        ) : (
          <>
            <AramaKutusu testid="stok-detay-urun-ara" placeholder="Ürün ara" value={arama} onChange={setArama} />
            {gorunenUrunler.length === 0 ? (
              <DetayMesaj testid="stok-detay-urun-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="stok-detay-urunler" className="w-full text-[13px] border-collapse">
                  <thead>
                    <tr className="text-[12px] text-ink-500 border-b border-border">
                      <th className="text-left font-medium py-2 pr-3">Ürün</th>
                      <th className="text-left font-medium py-2 px-3">Kategori</th>
                      <th className="text-right font-medium py-2 px-3">Miktar</th>
                      <th className="text-right font-medium py-2 px-3">Tutar</th>
                      <th className="text-right font-medium py-2 pl-3">Kümülatif pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gorunenUrunler.map((u) => (
                      <tr key={u.stok_kodu} data-testid={`stok-detay-urun-${u.stok_kodu}`} className="border-b border-border last:border-b-0">
                        <td className="py-2 pr-3 text-ink-900">{u.urun}</td>
                        <td className="py-2 px-3 text-ink-700">{u.kategori}</td>
                        <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(u.miktar) || 0)}</td>
                        <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(u.tutar) || 0)}</td>
                        <td className="py-2 pl-3 text-right tnum whitespace-nowrap">
                          {u.kumulatif_pay === null ? '—' : oranMetni(Number(u.kumulatif_pay) * 100, 1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DetayBolum>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// S352 varyantları: abc / oluStok / devir / stokGun / subeFark. Hepsi kartın ÇEKTİĞİ sorguyu aynı
// parametrelerle yeniden ister (react-query önbelleğinden gelir, ikinci istek atılmaz); yalnız `abc`
// kendi kaynağını (990076, @SINIF) çağırır.
// ════════════════════════════════════════════════════════════════════════════════════════════

/** Kartların `useStokSorgu` çağrısıyla aynı filtre: şube ve kategori gönderilmez. */
function kartFiltresi(donem: Donem): StokSorguFiltresi {
  return { donem, sube: null, kategori: null };
}

function GeriDugmesi({ testid, etiket, onClick }: { testid: string; etiket: string; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="focus-ring self-start text-[12px] font-medium text-ink-700 hover:underline underline-offset-2"
    >
      ← {etiket}
    </button>
  );
}

// ── abc: sınıf → ürün grupları → ürünler (990076, @BASTARIH/@BITTARIH + @SINIF) ─────────────────
// Sınıflama ds'te 990045 ile aynı kuralla yapılır; kartın satırındaki ürün sayısı ve ciro bu listenin
// satır sayısı ve toplamıdır. Grup kırılımı aynı satırlardan burada kurulur (ikinci sorgu yok).

interface AbcUrunSatiri { stok_kod: string; stok_ad: string; kategori: string; ciro: number }

function AbcDetay({ kaynaklar, donem, sinif }: Pick<GovdeProps, 'kaynaklar' | 'donem'> & { sinif: string }) {
  const kaynak = kaynaklar.abcDetay;
  const q = useDetaySorgu<AbcUrunSatiri>(kaynak, kartFiltresi(donem), { '@SINIF': sinif }, true);
  const [grup, setGrup] = useState<string | null>(null);
  const [arama, setArama] = useState('');

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">ABC ürün listesi bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">ABC ürünleri yüklenemedi.</DetayMesaj>;

  const satirlar = (q.data?.data ?? []).map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Bu sınıfta ürün yok.</DetayMesaj>;

  if (grup === null) {
    const harita = new Map<string, { urun: number; ciro: number }>();
    for (const s of satirlar) {
      const g = harita.get(s.kategori) ?? { urun: 0, ciro: 0 };
      g.urun += 1; g.ciro += Number(s.ciro) || 0;
      harita.set(s.kategori, g);
    }
    const gruplar = [...harita.entries()].map(([ad, v]) => ({ ad, ...v })).sort((x, y) => y.ciro - x.ciro);
    const toplamCiro = satirlar.reduce((t, s) => t + (Number(s.ciro) || 0), 0);
    return (
      <div className="flex flex-col gap-4">
        <OzetKutulari
          kutular={[
            { testid: 'stok-detay-ozet-grup', etiket: 'Ürün grubu', deger: fmtNum(gruplar.length) },
            { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(satirlar.length) },
            { testid: 'stok-detay-ozet-ciro', etiket: 'Ciro', deger: fmtTRY(toplamCiro) },
          ]}
        />
        <DetayBolum baslik="Ürün grupları">
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-abc-gruplar" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün grubu</th>
                  <th className="text-right font-medium py-2 px-3">Ürün</th>
                  <th className="text-right font-medium py-2 px-3">Ciro</th>
                  <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">Sınıftaki payı</th>
                </tr>
              </thead>
              <tbody>
                {gruplar.map((g) => (
                  <tr
                    key={g.ad}
                    data-testid={`stok-detay-abc-grup-${g.ad}`}
                    onClick={() => { setGrup(g.ad); setArama(''); }}
                    className="border-b border-border last:border-b-0 cursor-pointer group"
                  >
                    <td className="py-2 pr-3 text-ink-900">
                      <button type="button" className="focus-ring text-left group-hover:underline underline-offset-2">
                        {g.ad}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right tnum text-ink-700">{fmtNum(g.urun)}</td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(g.ciro)}</td>
                    <td className="py-2 pl-3 text-right tnum text-ink-700 whitespace-nowrap">
                      {toplamCiro > 0 ? oranMetni((g.ciro / toplamCiro) * 100, 1) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DetayBolum>
      </div>
    );
  }

  const grubun = satirlar.filter((s) => s.kategori === grup);
  const grupCiro = grubun.reduce((t, s) => t + (Number(s.ciro) || 0), 0);
  const gorunen = aramaSuz(grubun, arama, (s) => [s.stok_ad, s.stok_kod]);
  return (
    <div className="flex flex-col gap-4">
      <GeriDugmesi testid="stok-detay-abc-geri" etiket="Ürün grupları" onClick={() => setGrup(null)} />
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(grubun.length) },
          { testid: 'stok-detay-ozet-ciro', etiket: 'Ciro', deger: fmtTRY(grupCiro) },
        ]}
      />
      <DetayBolum baslik={grup}>
        <AramaKutusu testid="stok-detay-abc-ara" placeholder="Ürün/kod ara" value={arama} onChange={setArama} />
        {gorunen.length === 0 ? (
          <DetayMesaj testid="stok-detay-abc-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-abc-urunler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-left font-medium py-2 px-3">Kod</th>
                  <th className="text-right font-medium py-2 pl-3">Ciro</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.stok_kod} data-testid="stok-detay-abc-urun" className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 text-ink-900">{s.stok_ad}</td>
                    <td className="py-2 px-3 text-ink-500 tnum whitespace-nowrap">{s.stok_kod}</td>
                    <td className="py-2 pl-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.ciro) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayBolum>
    </div>
  );
}

// ── oluStok: türün ve gün kovasının ürünleri (990077, parametresiz) ──────────────────────────────

function OluStokDetay({
  kaynaklar, donem, kategori, grup, kova,
}: Pick<GovdeProps, 'kaynaklar' | 'donem' | 'kategori'> & { grup: OluTur; kova: string }) {
  const kaynak = kaynaklar.oluStokTur;
  const q = useDetaySorgu<OluStokSatiri>(kaynak, kartFiltresi(donem), {}, true);
  const [arama, setArama] = useState('');

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">Ölü stok listesi bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">Ölü stok ürünleri yüklenemedi.</DetayMesaj>;

  const satirlar = kategoriSuz(q.data?.data ?? [], kategori)
    .filter((s) => s.tur === grup && oluKovasi(Number(s.gun)) === kova)
    .map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }))
    .sort((a, b) => (Number(b.deger) || 0) - (Number(a.deger) || 0));
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Bu grupta ürün yok.</DetayMesaj>;

  const toplam = satirlar.reduce((t, s) => t + (Number(s.deger) || 0), 0);
  const gorunen = aramaSuz(satirlar, arama, (s) => [s.stok_ad, s.stok_kod, s.kategori]);
  const tarihBasligi = grup === 'satildi' ? 'Son satış' : 'İlk giriş';
  const gunBasligi = grup === 'satildi' ? 'Satmadığı gün' : 'Geldiğinden beri (gün)';

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(satirlar.length) },
          { testid: 'stok-detay-ozet-deger', etiket: 'Stok değeri', deger: fmtTRY(toplam) },
        ]}
      />
      <DetayBolum baslik="Ürünler">
        <AramaKutusu testid="stok-detay-olu-ara" placeholder="Ürün/kod/grup ara" value={arama} onChange={setArama} />
        {gorunen.length === 0 ? (
          <DetayMesaj testid="stok-detay-olu-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-olu-urunler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-left font-medium py-2 px-3">Ürün grubu</th>
                  <th className="text-left font-medium py-2 px-3 whitespace-nowrap">{tarihBasligi}</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">{gunBasligi}</th>
                  <th className="text-right font-medium py-2 px-3">Eldeki</th>
                  <th className="text-right font-medium py-2 pl-3">Değer</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.stok_kod} data-testid="stok-detay-olu-urun" className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 text-ink-900">{s.stok_ad}</td>
                    <td className="py-2 px-3 text-ink-700">{s.kategori}</td>
                    <td className="py-2 px-3 tnum whitespace-nowrap">{tarihEtiketi(String(s.referans_gun))}</td>
                    <td className="py-2 px-3 text-right tnum">{fmtNum(Number(s.gun) || 0)}</td>
                    <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.eldeki) || 0)}</td>
                    <td className="py-2 pl-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.deger) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayBolum>
    </div>
  );
}

// ── devir: grubun ürünleri, kaç günde döndüğüyle (990048, dönem) ─────────────────────────────────

function DevirDetay({ kaynaklar, donem, kategori }: Pick<GovdeProps, 'kaynaklar' | 'donem'> & { kategori: string }) {
  const kaynak = kaynaklar.devirHizi;
  const q = useDetaySorgu<DevirSatiri>(kaynak, kartFiltresi(donem), {}, true);
  const [arama, setArama] = useState('');

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">Stok dönüş süresi bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">Ürünler yüklenemedi.</DetayMesaj>;

  const satirlar = (q.data?.data ?? [])
    .filter((s) => kategoriNormalize(s.kategori) === kategori)
    .map((s) => ({
      ...s,
      gun: donusGunu(Number(s.etkin_gun) || 0, Number(s.donem_cogs) || 0, Number(s.ort_envanter_deger) || 0),
    }))
    // En yavaş dönen (en çok gün) üstte: bakılacak ürünler onlar. Satışı olmayan (gun null) en sonda.
    .sort((a, b) => (b.gun ?? -1) - (a.gun ?? -1));
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Bu grupta ürün yok.</DetayMesaj>;

  const etkinGun = Number(satirlar[0].etkin_gun) || 0;
  const maliyet = satirlar.reduce((t, s) => t + (Number(s.donem_cogs) || 0), 0);
  const stok = satirlar.reduce((t, s) => t + (Number(s.ort_envanter_deger) || 0), 0);
  const grupGun = donusGunu(etkinGun, maliyet, stok);
  const gorunen = aramaSuz(satirlar, arama, (s) => [s.stok_ad, s.stok_kod]);

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-gun', etiket: 'Grup kaç günde dönüyor', deger: grupGun !== null ? gunMetni(grupGun) : '-' },
          { testid: 'stok-detay-ozet-stok', etiket: 'Ortalama stok değeri', deger: fmtTRY(stok) },
          { testid: 'stok-detay-ozet-maliyet', etiket: 'Satılan malın maliyeti', deger: fmtTRY(maliyet) },
          { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(satirlar.length) },
        ]}
      />
      <DetayBolum baslik="Ürünler">
        <AramaKutusu testid="stok-detay-devir-ara" placeholder="Ürün/kod ara" value={arama} onChange={setArama} />
        {gorunen.length === 0 ? (
          <DetayMesaj testid="stok-detay-devir-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-devir-urunler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Ortalama stok değeri</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Satılan malın maliyeti</th>
                  <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">Kaç günde dönüyor</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.stok_kod} data-testid="stok-detay-devir-urun" className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 text-ink-900">{s.stok_ad}</td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.ort_envanter_deger) || 0)}</td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.donem_cogs) || 0)}</td>
                    <td className="py-2 pl-3 text-right tnum whitespace-nowrap">{s.gun !== null ? gunMetni(s.gun) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayBolum>
    </div>
  );
}

// ── stokGun: listenin tamamı (990047, dönem) ─────────────────────────────────────────────────────

const STOKGUN_BASLIK: Record<'bitecek' | 'asiri' | 'eksi', string> = {
  bitecek: 'Yakında bitecek',
  asiri: 'Aşırı stok',
  eksi: 'Stoğu sıfır ya da eksi görünen',
};

function StokGunDetay({ kaynaklar, donem, kategori, uc }: Pick<GovdeProps, 'kaynaklar' | 'donem' | 'kategori'> & { uc: 'bitecek' | 'asiri' | 'eksi' }) {
  const kaynak = kaynaklar.stokGun;
  const q = useDetaySorgu<StokGunSatiri>(kaynak, kartFiltresi(donem), {}, true);
  const [arama, setArama] = useState('');

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">Stok gün sayısı bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">Stok gün sayısı yüklenemedi.</DetayMesaj>;

  const ayrik = stokGunAyir(kategoriSuz(q.data?.data ?? [], kategori));
  const satirlar = ayrik[uc].map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Listede ürün yok.</DetayMesaj>;
  const gorunen = aramaSuz(satirlar, arama, (s) => [s.stok_ad, s.stok_kod, s.kategori]);

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[{ testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(satirlar.length) }]}
      />
      <DetayBolum baslik={uc === 'asiri' ? 'Ürünler (en çok gün yetenden)' : uc === 'bitecek' ? 'Ürünler (en az gün yetenden)' : 'Ürünler'}>
        <AramaKutusu testid="stok-detay-stokgun-ara" placeholder="Ürün/kod/grup ara" value={arama} onChange={setArama} />
        {gorunen.length === 0 ? (
          <DetayMesaj testid="stok-detay-stokgun-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-stokgun-urunler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-left font-medium py-2 px-3">Ürün grubu</th>
                  <th className="text-right font-medium py-2 px-3">Eldeki</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Günlük satış</th>
                  {uc !== 'eksi' && <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">Kalan gün</th>}
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.stok_kod} data-testid="stok-detay-stokgun-urun" className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 text-ink-900">{s.stok_ad}</td>
                    <td className="py-2 px-3 text-ink-700">{s.kategori}</td>
                    <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.eldeki) || 0)}</td>
                    <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.gunluk_satis) || 0)}</td>
                    {uc !== 'eksi' && (
                      <td className="py-2 pl-3 text-right tnum">{s.kalan_gun !== null ? miktarMetni(Number(s.kalan_gun)) : '-'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayBolum>
    </div>
  );
}

// ── subeFark: yalnız seçili şubede satılan ürünlerin tamamı (990078, dönem) ──────────────────────

function SubeFarkDetay({ kaynaklar, donem, kategori, sube }: Pick<GovdeProps, 'kaynaklar' | 'donem' | 'kategori'> & { sube: string }) {
  const kaynak = kaynaklar.subeFark;
  const q = useDetaySorgu<SubeFarkSatiri>(kaynak, kartFiltresi(donem), {}, true);
  const [arama, setArama] = useState('');

  if (!kaynak) return <DetayMesaj testid="stok-detay-kaynak-yok">Şubeler arası ürün farkı bu firmada tanımlı değil.</DetayMesaj>;
  if (q.isLoading) return <DetayYukleniyor testid="stok-detay-yukleniyor" />;
  if (q.isError) return <DetayMesaj testid="stok-detay-hata">Ürünler yüklenemedi.</DetayMesaj>;

  const satirlar = kategoriSuz(q.data?.data ?? [], kategori)
    .filter((s) => s.sube === sube)
    .map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }))
    .sort((a, b) => (Number(b.satis_tutar) || 0) - (Number(a.satis_tutar) || 0));
  if (satirlar.length === 0) return <DetayMesaj testid="stok-detay-bos">Bu şubeye özgü ürün yok.</DetayMesaj>;

  const toplam = satirlar.reduce((t, s) => t + (Number(s.satis_tutar) || 0), 0);
  const gorunen = aramaSuz(satirlar, arama, (s) => [s.stok_ad, s.stok_kod, s.kategori]);

  return (
    <div className="flex flex-col gap-4">
      <OzetKutulari
        kutular={[
          { testid: 'stok-detay-ozet-urun', etiket: 'Ürün sayısı', deger: fmtNum(satirlar.length) },
          { testid: 'stok-detay-ozet-ciro', etiket: 'Dönem satışı', deger: fmtTRY(toplam) },
        ]}
      />
      <DetayBolum baslik="Ürünler">
        <AramaKutusu testid="stok-detay-sube-ara" placeholder="Ürün/kod/grup ara" value={arama} onChange={setArama} />
        {gorunen.length === 0 ? (
          <DetayMesaj testid="stok-detay-sube-arama-bos">Aramaya uyan ürün yok.</DetayMesaj>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="stok-detay-sube-urunler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Ürün</th>
                  <th className="text-left font-medium py-2 px-3">Ürün grubu</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Satış miktarı</th>
                  <th className="text-right font-medium py-2 px-3">Satış</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Bu şubede stok</th>
                  <th className="text-right font-medium py-2 pl-3 whitespace-nowrap">Öbür şubede stok</th>
                </tr>
              </thead>
              <tbody>
                {gorunen.map((s) => (
                  <tr key={s.stok_kod} data-testid="stok-detay-sube-urun" className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-3 text-ink-900">{s.stok_ad}</td>
                    <td className="py-2 px-3 text-ink-700">{s.kategori}</td>
                    <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.satis_miktar) || 0)}</td>
                    <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(Number(s.satis_tutar) || 0)}</td>
                    <td className="py-2 px-3 text-right tnum">{miktarMetni(Number(s.sube_stok) || 0)}</td>
                    <td className="py-2 pl-3 text-right tnum">{miktarMetni(Number(s.diger_stok) || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetayBolum>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Modal kabuğu
// ════════════════════════════════════════════════════════════════════════════════════════════

function detayBasligi(d: StokDetay): string {
  switch (d.tur) {
    case 'kayipUrun':
      return d.stokAd;
    case 'kayipTumu':
      return 'Fire veren tüm ürünler';
    case 'kayipGun':
      return d.kova === 'gun' ? tarihEtiketi(d.gun) : ayEtiketi(d.ay);
    case 'abc':
      return `ABC Analizi · ${d.sinif} sınıfı`;
    case 'oluStok':
      return `Ölü stok · ${OLU_TURLERI.find((t) => t.tur === d.grup)?.baslik ?? ''} · ${d.kova}`;
    case 'devir':
      return `Stok kaç günde dönüyor · ${d.kategori}`;
    case 'stokGun':
      return `Stok gün sayısı · ${STOKGUN_BASLIK[d.uc]}`;
    case 'subeFark':
      return `Yalnız ${d.sube} şubesinde satılan ürünler`;
  }
}

function StokDetayGovde({ d, ...p }: { d: StokDetay } & GovdeProps) {
  switch (d.tur) {
    case 'kayipUrun':
      return <KayipUrunDetay {...p} stokKod={d.stokKod} />;
    case 'kayipTumu':
      return <KayipTumUrunler kaynaklar={p.kaynaklar} donem={p.donem} sube={p.sube} kategori={p.kategori} />;
    case 'kayipGun':
      return <KayipGunDetay kaynaklar={p.kaynaklar} sube={p.sube} kategori={p.kategori} pencere={kayipGunPenceresi(d)} />;
    case 'abc':
      return <AbcDetay kaynaklar={p.kaynaklar} donem={p.donem} sinif={d.sinif} />;
    case 'oluStok':
      return <OluStokDetay kaynaklar={p.kaynaklar} donem={p.donem} kategori={p.kategori} grup={d.grup} kova={d.kova} />;
    case 'devir':
      return <DevirDetay kaynaklar={p.kaynaklar} donem={p.donem} kategori={d.kategori} />;
    case 'stokGun':
      return <StokGunDetay kaynaklar={p.kaynaklar} donem={p.donem} kategori={p.kategori} uc={d.uc} />;
    case 'subeFark':
      return <SubeFarkDetay kaynaklar={p.kaynaklar} donem={p.donem} kategori={p.kategori} sube={d.sube} />;
  }
}

type Props = { detay: StokDetay | null; onKapat: () => void } & GovdeProps;

/**
 * Modal KAPALIYKEN (`detay === null`) govde hiç MOUNT OLMAZ → hiçbir sorgu açılmaz (`destroyOnHidden`
 * ile antd kabuğu da kapanınca atılır) — SA'nın DetayModal'ındaki AYNI kural.
 */
export function StokDetayModal({ detay, onKapat, kaynaklar, donem, sube, kategori }: Props) {
  return (
    <Modal
      open={detay !== null}
      onCancel={onKapat}
      footer={null}
      centered
      width={{ xs: '100%', sm: 'min(960px, 94vw)' }}
      destroyOnHidden
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      title={detay && <span data-testid="stok-detay-baslik">{detayBasligi(detay)}</span>}
    >
      {detay && (
        <div data-testid="stok-detay-modal" data-tur={detay.tur}>
          <StokDetayGovde d={detay} kaynaklar={kaynaklar} donem={donem} sube={sube} kategori={kategori} />
        </div>
      )}
    </Modal>
  );
}
